import React, { useMemo, useState } from 'react';
import packageJson from '../../package.json';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { apiUrl } from '../utils/apiBase';

const HELP_URL_BY_LANGUAGE = {
  en: 'https://salesign.app/help/',
  ru: 'https://salesign.app/help/',
  de: 'https://salesign.app/de/hilfe-und-support/',
  es: 'https://salesign.app/es/ayuda-y-soporte/',
  pt: 'https://salesign.app/pt/ajuda-e-suporte/',
  zh: 'https://salesign.app/zh/%e5%b8%ae%e5%8a%a9%e4%b8%8e%e6%94%af%e6%8c%81/',
  fr: 'https://salesign.app/fr/aide-et-support/',
  ar: 'https://salesign.app/ar/614-2/',
};

const VersionFooter = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const helpUrl = HELP_URL_BY_LANGUAGE[i18n.language] ?? HELP_URL_BY_LANGUAGE.en;
  const buildDate = new Date().toLocaleDateString();
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackType, setFeedbackType] = useState('feedback');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');

  const pageUrl = useMemo(() => window.location.href || location.pathname, [location]);
  const systemInfo = useMemo(() => navigator.userAgent || 'Unknown device', []);

  const resetForm = () => {
    setFeedbackType('feedback');
    setFeedbackMessage('');
    setSubmitError('');
    setSubmitSuccess('');
  };

  const handleOpenModal = () => {
    resetForm();
    setShowFeedbackModal(true);
  };

  const handleCloseModal = () => {
    setShowFeedbackModal(false);
  };

  const handleSubmitFeedback = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    setSubmitSuccess('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(apiUrl('/feedback'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          type: feedbackType,
          message: feedbackMessage,
          page_url: pageUrl,
          system_info: systemInfo,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to send feedback.');
      }

      setSubmitSuccess('Thank you! Your feedback has been sent to the admin team.');
      resetForm();
      setShowFeedbackModal(false);
    } catch (error) {
      setSubmitError(error.message || 'Failed to send feedback. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <footer className="bg-gray-50 border-t border-gray-200 py-4 px-4 mt-auto">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-center text-sm text-gray-600">
          <div className="flex items-center space-x-4 mb-2 sm:mb-0">
            <span className="font-medium text-gray-800">SaleSign</span>
            <span className="text-gray-400">•</span>
            <span className="font-mono text-xs bg-gray-200 px-2 py-1 rounded">
              v{packageJson.version}
            </span>
            <span className="text-gray-400">•</span>
            <span className="text-xs text-gray-500">
              Build {buildDate}
            </span>
          </div>
          
          <div className="flex items-center space-x-4">
            <span>© 2025 Ventaplaza.com</span>
            <span className="text-gray-400">•</span>
            <a
              href={helpUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded-full transition-colors no-underline"
            >
              ❓ {t('common.help')}
            </a>
            {isAuthenticated && (
              <button
                onClick={handleOpenModal}
                className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-full transition-colors"
              >
                💬 Send Feedback
              </button>
            )}
          </div>
        </div>
        
        {/* Removed tech stack line per request */}
      </div>

      {showFeedbackModal && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-lg mx-4 z-[2100]">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Send Feedback</h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-500 hover:text-gray-700 transition-colors"
                aria-label="Close feedback modal"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmitFeedback} className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Feedback Type
                </label>
                <select
                  value={feedbackType}
                  onChange={(e) => setFeedbackType(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="feedback">General Feedback</option>
                  <option value="bug">Bug Report</option>
                  <option value="issue">Technical Issue</option>
                  <option value="feature">Feature Request</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message
                </label>
                <textarea
                  value={feedbackMessage}
                  onChange={(e) => setFeedbackMessage(e.target.value)}
                  rows={5}
                  placeholder="Tell us what happened or share your ideas..."
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {submitError && (
                <p className="text-sm text-red-600">{submitError}</p>
              )}
              {submitSuccess && (
                <p className="text-sm text-green-600">{submitSuccess}</p>
              )}

              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !feedbackMessage.trim()}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? 'Sending...' : 'Send Feedback'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </footer>
  );
};

export default VersionFooter;
